import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppSelector } from '@store/store.tsx';
import {
  selectPlanCapabilities,
  selectSiteApps,
  selectSiteFeatureFlags,
} from '@store/slices/settingsSlice.tsx';
import { selectIsStaff, selectIsSuperuser, selectUserGroups, selectUserPermissions, selectUserRoles } from '@store/slices/userSlice.tsx';

import {
  DASHBOARD_NAV_ITEMS,
  getVisibleNavItems,
} from '../registry/dashboardNavRegistry.ts';
import type { DashboardNavGroup, DashboardNavItem } from '../registry/types.ts';
import { useDashboardShortcuts } from './useDashboardShortcuts.ts';

export type CommandPaletteItem =
  | {
      id: string;
      type: 'navigation';
      title: string;
      description?: string;
      icon: DashboardNavItem['icon'];
      to: string;
      group: DashboardNavGroup;
      keywords: string;
      groupLabel: string;
    }
  | {
      id: string;
      type: 'action';
      title: string;
      description?: string;
      icon?: DashboardNavItem['icon'];
      perform: () => void;
      relatedKey?: string;
      group?: DashboardNavGroup;
      keywords: string;
    };

export interface UseCommandPaletteReturn {
  isOpen: boolean;
  open: (options?: { group?: DashboardNavGroup; query?: string }) => void;
  close: () => void;
  query: string;
  setQuery: (value: string) => void;
  highlightedIndex: number;
  setHighlightedIndex: (index: number) => void;
  moveHighlight: (delta: number) => void;
  results: CommandPaletteItem[];
  selectItem: (item: CommandPaletteItem) => void;
  groupFilter?: DashboardNavGroup;
}

const GROUP_LABELS: Record<DashboardNavGroup, string> = {
  general: 'General',
  eshop: 'E-shop',
  cms: 'CMS',
  erp: 'ERP',
};

const sanitize = (value: string) => value.trim().toLowerCase();

const buildKeywords = (item: DashboardNavItem, groupLabel: string) =>
  [item.title, item.description ?? '', groupLabel, item.key.replace(/\./g, ' ')].map(sanitize).join(' ');

const shouldIgnoreTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tag = target.tagName.toLowerCase();
  return target.isContentEditable || tag === 'input' || tag === 'textarea' || tag === 'select';
};

const scoreFor = (keywords: string, queryTokens: string[]) => {
  let total = 0;
  for (const token of queryTokens) {
    const index = keywords.indexOf(token);
    if (index === -1) {
      return Number.POSITIVE_INFINITY;
    }
    total += index;
  }
  return total;
};

export const useCommandPalette = (): UseCommandPaletteReturn => {
  const siteApps = useAppSelector(selectSiteApps) ?? [];
  const siteFeatures = useAppSelector(selectSiteFeatureFlags) ?? {};
  const planCapabilities = useAppSelector(selectPlanCapabilities);
  const isStaff = useAppSelector(selectIsStaff);
  const isSuperuser = useAppSelector(selectIsSuperuser);
  const permissions = useAppSelector(selectUserPermissions) ?? [];
  const groups = useAppSelector(selectUserGroups) ?? [];
  const roles = useAppSelector(selectUserRoles) ?? [];

  const navigate = useNavigate();
  const { toggleFavorite, isFavorite } = useDashboardShortcuts();

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [groupFilter, setGroupFilter] = useState<DashboardNavGroup | undefined>(undefined);

  const visibleItems = useMemo(() => {
    return getVisibleNavItems(DASHBOARD_NAV_ITEMS, {
      apps: siteApps,
      features: siteFeatures,
      isStaff,
      isSuperuser,
      permissions,
      groups,
      roles,
      planCapabilities,
    });
  }, [groups, isStaff, isSuperuser, permissions, planCapabilities, roles, siteApps, siteFeatures]);

  const navigationCommands = useMemo(() => {
    return visibleItems.map<CommandPaletteItem>((item) => {
      const groupLabel = GROUP_LABELS[item.group];
      return {
        id: `nav:${item.key}`,
        type: 'navigation',
        title: item.title,
        description: item.description,
        icon: item.icon,
        to: item.to,
        group: item.group,
        groupLabel,
        keywords: buildKeywords(item, groupLabel),
      };
    });
  }, [visibleItems]);

  const commands = useMemo(() => {
    const actionItems: CommandPaletteItem[] = navigationCommands.map((command) => {
      const navKey = command.id.replace('nav:', '');
      const favorite = isFavorite(navKey);
      const baseTitle = favorite ? 'Quitar de favoritos' : 'Agregar a favoritos';

      return {
        id: `favorite:${command.id}`,
        type: 'action',
        title: `${baseTitle} · ${command.title}`,
        description: command.description,
        icon: command.icon,
        perform: () => toggleFavorite(navKey),
        relatedKey: navKey,
        group: command.group,
        keywords: `${baseTitle.toLowerCase()} ${command.keywords}`,
      };
    });

    return [...navigationCommands, ...actionItems];
  }, [isFavorite, navigationCommands, toggleFavorite]);

  const results = useMemo(() => {
    const normalizedQuery = sanitize(query);
    const tokens = normalizedQuery ? normalizedQuery.split(/\s+/).filter(Boolean) : [];

    const pool = tokens.length ? commands : navigationCommands;

    const filteredByGroup = groupFilter
      ? pool.filter((item) => (item.type === 'navigation' ? item.group === groupFilter : item.group === groupFilter))
      : pool;

    if (!tokens.length) {
      return filteredByGroup.slice(0, 12);
    }

    return filteredByGroup
      .map((item) => ({ item, score: scoreFor(item.keywords, tokens) }))
      .filter(({ score }) => Number.isFinite(score))
      .sort((a, b) => a.score - b.score)
      .map(({ item }) => item)
      .slice(0, 12);
  }, [commands, groupFilter, navigationCommands, query]);

  const open = useCallback(
    (options?: { group?: DashboardNavGroup; query?: string }) => {
      setIsOpen(true);
      setHighlightedIndex(0);
      setQuery(options?.query ?? '');
      setGroupFilter(options?.group);
    },
    [],
  );

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setHighlightedIndex(0);
    setGroupFilter(undefined);
  }, []);

  const selectItem = useCallback(
    (item: CommandPaletteItem) => {
      if (item.type === 'navigation') {
        close();
        navigate(item.to);
        return;
      }

      item.perform();
      close();
    },
    [close, navigate],
  );

  const moveHighlight = useCallback(
    (delta: number) => {
      if (!results.length) {
        return;
      }

      setHighlightedIndex((current) => {
        const next = (current + delta + results.length) % results.length;
        return next;
      });
    },
    [results.length],
  );

  const sequenceRef = useRef<string[]>([]);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    const resetSequence = () => {
      sequenceRef.current = [];
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const pushSequence = (key: string) => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }

      sequenceRef.current.push(key);
      timerRef.current = window.setTimeout(() => {
        resetSequence();
      }, 600);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) {
        return;
      }

      const key = event.key?.toLowerCase();

      if ((event.metaKey || event.ctrlKey) && key === 'k') {
        event.preventDefault();
        open();
        return;
      }

      if (key === 'escape' && isOpen) {
        event.preventDefault();
        close();
        return;
      }

      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }

      if (key === '/' && !shouldIgnoreTarget(event.target)) {
        event.preventDefault();
        open();
        return;
      }

      if (shouldIgnoreTarget(event.target)) {
        return;
      }

      if (key === 'g') {
        event.preventDefault();
        pushSequence('g');
        if (sequenceRef.current.length === 2 && sequenceRef.current.every((value) => value === 'g')) {
          open({ group: 'general' });
          resetSequence();
        }
        return;
      }

      if (key === 'e') {
        if (sequenceRef.current.length === 1 && sequenceRef.current[0] === 'g') {
          event.preventDefault();
          open({ group: 'eshop' });
        }
        resetSequence();
        return;
      }

      resetSequence();
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      resetSequence();
    };
  }, [close, isOpen, open]);

  useEffect(() => {
    if (groupFilter && query.trim().length) {
      setGroupFilter(undefined);
    }
  }, [groupFilter, query]);

  return {
    isOpen,
    open,
    close,
    query,
    setQuery,
    highlightedIndex,
    setHighlightedIndex,
    moveHighlight,
    results,
    selectItem,
    groupFilter,
  };
};

export type { DashboardNavGroup } from '../registry/types.ts';
